import { Duration } from "luxon"
import { changeAccount, getUserAccounts, sendConfirmationLink } from "mobile/Api/GetUserAccounts"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { selectCurrentEnvironment } from "shared/Modules/Environment/envSelectors"
import { completeAuthentication } from "shared/Modules/Login/loginActions"
import { useToken } from "shared/Modules/Login/useToken"
import { useApiCall } from "shared/Modules/Query/useApiCall"
import { useQueryWithOptions } from "shared/Modules/Query/useQuery"
import { selectUser } from "shared/Modules/User/userSelectors"
import { UserAccount, UserAccountsResponse } from "./changeAccountTypes"
import { isAuthenticationComplete } from "shared/Modules/Login/loginTypes"
import { useSnackbar } from "notistack"
import { useLocalization } from "@fluent/react"
import { useAppNavigation } from "shared/hooks/useAppNavigation"
import { useInterval } from "shared/hooks/useInterval"
import { logout } from "shared/Modules/User/userActions"
import { useSystemLinks } from "shared/Modules/Cordova/hooks/useAppLinks"
import { selectLinkToSupportEmail } from "shared/Modules/Properties/propertySelectors"

type UseSwitchAccountReturn = {
    userData: UserAccountsResponse | null
    isUserDataLoading: boolean
    sendConfirmLink: () => void
    contactSupport: () => void
    isConfirmSend: boolean
    isUserDataError: boolean
    hasAccounts: boolean
    selectedAccountName: string
    onAccountClick: (account: UserAccount) => void
}

const POLLING_INTERVAL = Duration.fromObject({ seconds: 3 })

export const useSwitchAccount = (): UseSwitchAccountReturn => {
    const environment = useSelector(selectCurrentEnvironment)
    const user = useSelector(selectUser)
    const { goToHome } = useAppNavigation()
    const [isConfirmSend, setIsConfirmSend] = useState(false)
    const [selectedAccountName, setSelectedAccountName] = useState("")
    const supportEmail = useSelector(selectLinkToSupportEmail) ?? "mailto:support@facilitynet.dk"
    const token = useToken()
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()
    const { l10n } = useLocalization()
    const openSystemLink = useSystemLinks()
    const userId = user?.id ?? null

    const { callForResult: sendLink, handleCallError: handleConfirmError } = useApiCall(sendConfirmationLink)
    const { callForResult: callChangeAccount, handleCallError: handleChangeAccountError } = useApiCall(changeAccount)
    const { response: usersResponse, refresh } = useQueryWithOptions(
        () => getUserAccounts(token, environment, userId!),
        {
            dependencies: [token, environment, userId],
            shouldGet: Boolean(token && userId),
        }
    )

    const contactSupport = useCallback(() => openSystemLink(supportEmail), [supportEmail])

    const onAccountClick = (account: UserAccount) => {
        setSelectedAccountName(account.user.username)
        callChangeAccount(
            {
                user: {
                    username: account.user.username,
                },
            },
            userId!,
            token,
            environment
        )
            .then((data) => {
                if (isAuthenticationComplete(data)) {
                    const newAuthData = {
                        userId: data.user.id,
                        companyId: data.user.companyId,
                        userUid: data.user.uid,
                        token: data.authentication.token,
                        username: data.user.username,
                    }
                    dispatch(logout(false, undefined, false))
                    dispatch(completeAuthentication(newAuthData))
                    goToHome()
                } else {
                    setSelectedAccountName("")
                    enqueueSnackbar(l10n.getString("me-switch-account-invalid-authentication"), {
                        variant: "error",
                        preventDuplicate: true,
                    })
                }
            })
            .catch((e) => {
                setSelectedAccountName("")
                handleChangeAccountError(e, "switching account")
            })
    }

    const sendConfirmLink = useCallback(() => {
        sendLink(token, environment, user?.id!)
            .then(() => {
                setIsConfirmSend(true)
            })
            .catch((e) => handleConfirmError(e, "sending confirmation link"))
        setIsConfirmSend(true)
    }, [token, environment, user?.id])

    const userData = useMemo(() => {
        if (!usersResponse.loading && !usersResponse.failed) {
            return usersResponse.response.data
        }

        return null
    }, [usersResponse])

    useInterval(refresh, isConfirmSend && !!userData?.authenticationResult ? POLLING_INTERVAL.as("milliseconds") : null)

    return {
        selectedAccountName,
        sendConfirmLink,
        isConfirmSend,
        isUserDataLoading: usersResponse.loading,
        onAccountClick,
        contactSupport,
        hasAccounts: !!userData?.userAccountGroups?.length && !userData?.authenticationResult,
        userData: !usersResponse.loading && !usersResponse.failed ? usersResponse.response.data : null,
        isUserDataError: !usersResponse.loading && usersResponse.failed,
    }
}
